import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  List,
  ListItem,
} from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  People as PeopleIcon,
  VideoCall as VideoCallIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import {
  getCalendarEvents,
  blacklistCalendarEvent,
  unblacklistCalendarEvent,
  CalendarEvent,
} from '../services/apiService';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '../constants/dateFormats';

interface EventsListProps {
  autoJoinEnabled: boolean;
}

const EventsList: React.FC<EventsListProps> = ({ autoJoinEnabled }) => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'1week' | '2weeks' | '1month'>('1week');
  const [tabValue, setTabValue] = useState<number>(0); // 0 = Upcoming, 1 = Bot Cancelled
  const [actionLoading, setActionLoading] = useState<string | null>(null); // Track which event is being processed
  const cardRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (autoJoinEnabled) {
      fetchEvents();
    }
  }, [autoJoinEnabled, timeRange]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCalendarEvents(timeRange);
      setEvents(response.events || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch calendar events');
    } finally {
      setLoading(false);
    }
  };

  const handleBlacklist = async (eventId: string) => {
    try {
      setError(null);
      setSuccess(null);
      setActionLoading(eventId);
      await blacklistCalendarEvent(eventId);
      setSuccess('Bot removed from event successfully');
      scrollToTop();
      setTimeout(() => setSuccess(null), 5000);
      // Refresh events
      await fetchEvents();
    } catch (err: any) {
      // Show the specific error message from the backend
      const errorMessage = err.error || err.message || 'Failed to remove bot from event';
      setError(errorMessage);
      scrollToTop();
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblacklist = async (eventId: string) => {
    try {
      setError(null);
      setSuccess(null);
      setActionLoading(eventId);
      await unblacklistCalendarEvent(eventId);
      setSuccess('Bot re-enabled for event');
      scrollToTop();
      setTimeout(() => setSuccess(null), 5000);
      // Refresh events
      await fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Failed to re-enable bot for event');
      scrollToTop();
    } finally {
      setActionLoading(null);
    }
  };

  const formatDateTime = (dateString: string) => {
    return moment(dateString).format(DATE_TIME_FORMAT);
  };

  // Filter events based on selected tab
  const upcomingEvents = events.filter(e => !e.isBlacklisted);
  const blacklistedEvents = events.filter(e => e.isBlacklisted);

  if (!autoJoinEnabled) {
    return (
      <Card elevation={1} sx={{ maxWidth: '800px', mt: 4 }}>
        <CardContent className="p-6">
          <Alert severity="info">
            Enable Auto-Join to view and manage your calendar events.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={cardRef} elevation={1} sx={{ maxWidth: '800px', mt: 4 }}>
      <CardContent className="p-6">
        <Box className="flex justify-between items-center mb-4">
          <Typography variant="h6">Calendar Events</Typography>
          <Box className="flex items-center space-x-2">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value as '1week' | '2weeks' | '1month')}
              >
                <MenuItem value="1week">1 Week</MenuItem>
                <MenuItem value="2weeks">2 Weeks</MenuItem>
                <MenuItem value="1month">1 Month</MenuItem>
              </Select>
            </FormControl>
            <IconButton onClick={fetchEvents} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="mb-4" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} className="mb-4">
          <Tab
            label={
              <Box className="flex items-center">
                Upcoming
                <Chip size="small" label={upcomingEvents.length} sx={{ ml: 1 }} />
              </Box>
            }
          />
          <Tab
            label={
              <Box className="flex items-center">
                Bot Cancelled
                <Chip size="small" label={blacklistedEvents.length} sx={{ ml: 1 }} />
              </Box>
            }
          />
        </Tabs>

        <Divider className="mb-4" />

        {loading ? (
          <Box className="flex justify-center p-8">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Upcoming Events Tab */}
            {tabValue === 0 && (
              <Box>
                {upcomingEvents.length === 0 ? (
                  <Box className="text-center py-8">
                    <Typography variant="body1" color="textSecondary">
                      No upcoming events found for the selected time range.
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {upcomingEvents.map((event, index) => (
                      <React.Fragment key={event.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            py: 2,
                          }}
                        >
                          <Box className="w-full flex justify-between items-start">
                            <Box className="flex-1">
                              <Box className="flex items-center mb-1">
                                <EventIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
                                <Typography variant="body1" fontWeight="medium">
                                  {event.title}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="textSecondary" className="mb-2">
                                {formatDateTime(event.startTime)}
                              </Typography>
                              {event.attendees && event.attendees.length > 0 && (
                                <Box className="flex items-center mb-1">
                                  <PeopleIcon fontSize="small" sx={{ mr: 1 }} color="action" />
                                  <Typography variant="caption" color="textSecondary">
                                    {event.attendees.length} attendee{event.attendees.length !== 1 ? 's' : ''}
                                  </Typography>
                                </Box>
                              )}
                              {event.meetingUrl && (
                                <Box className="flex items-center">
                                  <VideoCallIcon fontSize="small" sx={{ mr: 1 }} color="action" />
                                  <Typography variant="caption" color="textSecondary">
                                    Bot will auto-join
                                  </Typography>
                                </Box>
                              )}
                              {event.hasBotScheduled && event.botStatus && (
                                <Box className="flex items-center mt-1">
                                  <BotIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
                                  <Chip
                                    size="small"
                                    label={`Bot ${event.botStatus}`}
                                    color={
                                      event.botStatus === 'active' ? 'success' :
                                      event.botStatus === 'joining' ? 'warning' :
                                      'info'
                                    }
                                  />
                                </Box>
                              )}
                            </Box>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={actionLoading === event.id ? <CircularProgress size={16} color="inherit" /> : <BlockIcon />}
                              onClick={() => handleBlacklist(event.id)}
                              disabled={actionLoading !== null}
                            >
                              {actionLoading === event.id ? 'Removing...' : 'Remove Bot'}
                            </Button>
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}

            {/* Blacklisted Events Tab */}
            {tabValue === 1 && (
              <Box>
                {blacklistedEvents.length === 0 ? (
                  <Box className="text-center py-8">
                    <Typography variant="body1" color="textSecondary">
                      No events with cancelled bots.
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {blacklistedEvents.map((event, index) => (
                      <React.Fragment key={event.id}>
                        {index > 0 && <Divider />}
                        <ListItem
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            py: 2,
                            bgcolor: '#fafafa',
                          }}
                        >
                          <Box className="w-full flex justify-between items-start">
                            <Box className="flex-1">
                              <Box className="flex items-center mb-1">
                                <EventIcon fontSize="small" sx={{ mr: 1 }} color="disabled" />
                                <Typography variant="body1" fontWeight="medium">
                                  {event.title}
                                </Typography>
                                <Chip
                                  size="small"
                                  label="Bot Cancelled"
                                  color="error"
                                  sx={{ ml: 2 }}
                                />
                              </Box>
                              <Typography variant="body2" color="textSecondary" className="mb-2">
                                {formatDateTime(event.startTime)}
                              </Typography>
                              {event.attendees && event.attendees.length > 0 && (
                                <Box className="flex items-center mb-1">
                                  <PeopleIcon fontSize="small" sx={{ mr: 1 }} color="action" />
                                  <Typography variant="caption" color="textSecondary">
                                    {event.attendees.length} attendee{event.attendees.length !== 1 ? 's' : ''}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={actionLoading === event.id ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                              onClick={() => handleUnblacklist(event.id)}
                              disabled={actionLoading !== null}
                            >
                              {actionLoading === event.id ? 'Re-enabling...' : 'Re-enable Bot'}
                            </Button>
                          </Box>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </>
        )}

        <Box className="mt-4 p-3 bg-blue-50 rounded">
          <Typography variant="caption" color="textSecondary">
            <strong>Note:</strong> Events with cancelled bots will not have bots automatically join. You can
            still manually create a bot for these events if needed.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EventsList;
